import type { MeasurementPoint, PointReading } from "@energyplatform/client";
import { api } from "./services.ts";
import type { Sample } from "./analysis.ts";

// Series of a measurement point from the device channels that measure it now. Shared by the point card and the
// analyses, so both read a point the same way.

/**
 * A quantity over several channels (e.g. power of the measured phases) at 15 min: per step the sum of the channels
 * that have a value there; a step where none has one is a gap.
 */
export async function summed(
  deviceId: string,
  channels: string[],
  attribute: string,
  from: Date,
  to: Date,
): Promise<{ at: string; value: number | null }[]> {
  const all = await Promise.all(
    channels.map((c) =>
      api.devices.series(deviceId, c, attribute, from, to, "15m"),
    ),
  );
  const byAt = new Map<string, number | null>();
  for (const p of all.flat()) {
    const prev = byAt.get(p.at) ?? null;
    byAt.set(p.at, p.value === null ? prev : (prev ?? 0) + p.value);
  }
  return [...byAt]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([at, value]) => ({ at, value }));
}

const ENERGY_MEDIA = new Set(["el", "pv", "bess"]);

export interface PointSeries {
  point: MeasurementPoint;
  attribute: string;
  unit: string;
  /** The main quantity at 15 min. */
  values: Sample[];
  /**
   * Energy per bucket (kWh, at the bucket's start), for meters: 15 min for up to a week, hourly beyond; as the balance
   * counts it (all bindings, balanced registers). Empty otherwise.
   */
  energy: Sample[];
}

/**
 * The point's main quantity and, for electricity, its energy over [from, to), from the current device and only
 * since the binding started (older devices are not mixed in).
 */
export async function pointSeries(
  point: MeasurementPoint,
  reading: PointReading | undefined,
  from: Date,
  to: Date,
): Promise<PointSeries | null> {
  const b = point.binding;
  const meter = ENERGY_MEDIA.has(point.medium);
  const attribute = reading?.attribute ?? (meter ? "power.active" : null);
  if (!b || !attribute) {
    return null;
  }
  const start = new Date(
    Math.max(from.getTime(), new Date(b.validFrom).getTime()),
  );
  const end = new Date(
    Math.max(start.getTime() + 1, Math.min(to.getTime(), Date.now())),
  );
  const channels =
    reading && reading.channels.length > 0 ? reading.channels : [b.channel];
  const [values, counter] = await Promise.all([
    summed(b.deviceId, channels, attribute, start, end),
    meter
      ? api.sites.pointEnergy(
          point.id,
          start,
          end,
          end.getTime() - start.getTime() <= 7 * 86_400_000 ? "15m" : "hour",
        )
      : Promise.resolve([]),
  ]);
  return {
    point,
    attribute,
    unit: reading?.unit ?? (meter ? "kW" : ""),
    values: values.map((p) => ({ t: new Date(p.at).getTime(), v: p.value })),
    energy: counter.map((p) => ({ t: new Date(p.at).getTime(), v: p.kwh })),
  };
}
